// src/pages/ProgramDetail.js
// ──────────────────────────────────────────────────────────────────────────
// Single-program page. Pulls the program by id (sample-data fallback),
// shows a hero, key facts sidebar, overview, and an apply CTA.
// ──────────────────────────────────────────────────────────────────────────

import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import styled from 'styled-components';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import ProgramsAPI from '../api/programs';
import theme from '../theme/theme';

const Hero = styled.header`
  position: relative;
  min-height: 52vh;
  display: flex;
  align-items: flex-end;
  padding: 3rem ${theme.layout.gutter};
  color: #fff;
  background: #222 url(${(p) => p.$src}) center/cover no-repeat;
  &::after {
    content: '';
    position: absolute; inset: 0;
    background: linear-gradient(180deg, rgba(0,0,0,0.2), rgba(0,0,0,0.75));
  }
  > * { position: relative; z-index: 1; }
`;

const Crumb = styled(Link)`
  color: rgba(255,255,255,0.8);
  font-size: 0.85rem;
  &:hover { color: #fff; }
`;

const Title = styled.h1`
  color: #fff;
  font-size: clamp(2.2rem, 6vw, 4rem);
  max-width: 18ch;
  margin-top: 0.5rem;
`;

const Wrap = styled.div`
  max-width: ${theme.layout.maxWidth};
  margin: 0 auto;
  padding: 3.5rem ${theme.layout.gutter} 6rem;
  display: grid;
  grid-template-columns: 1.7fr 1fr;
  gap: 3rem;
  @media (max-width: 820px) { grid-template-columns: 1fr; }
`;

const Overview = styled.div`
  h2 { font-size: 1.8rem; margin-bottom: 1rem; }
  p  { color: ${theme.color.inkSoft}; margin-bottom: 1.1rem; }
`;

const Facts = styled.aside`
  align-self: start;
  border: 1px solid ${theme.color.line};
  border-radius: ${theme.layout.radius};
  background: #fff;
  padding: 1.6rem;
`;

const Fact = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.7rem 0;
  border-bottom: 1px solid ${theme.color.line};
  font-size: 0.92rem;
  &:last-of-type { border-bottom: none; }
  span:first-child { color: ${theme.color.inkSoft}; }
  span:last-child  { font-weight: 600; text-align: right; }
`;

const ApplyBtn = styled(Link)`
  display: block;
  text-align: center;
  margin-top: 1.4rem;
  background: ${theme.color.accent};
  color: ${theme.color.onAccent};
  font-weight: 600;
  padding: 13px;
  border-radius: ${theme.layout.radius};
  &:hover { background: ${theme.color.accentDark}; }
`;

const Center = styled.div`
  max-width: ${theme.layout.maxWidth};
  margin: 0 auto;
  padding: 6rem ${theme.layout.gutter};
  text-align: center;
  color: ${theme.color.inkSoft};
`;

const ProgramDetail = () => {
  const { id } = useParams();
  const [program, setProgram] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    ProgramsAPI.getOne(id)
      .then((p) => { if (alive) setProgram(p); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [id]);

  if (loading) return (<><Navbar /><Center>Loading…</Center></>);
  if (!program) return (
    <>
      <Navbar />
      <Center>
        Program not found. <Link to="/programs" style={{ color: theme.color.accent }}>Back to programs →</Link>
      </Center>
    </>
  );

  return (
    <>
      <Navbar />
      <Hero $src={program.image}>
        <div>
          <Crumb to="/programs">← All programs</Crumb>
          <Title>{program.title}</Title>
        </div>
      </Hero>

      <Wrap>
        <Overview>
          <h2>Overview</h2>
          <p>{program.summary}</p>
          <p>
            This is a hands-on course — about 80% of your time is practical, using real tools and
            equipment with qualified instructors. You also get entrepreneurship training so you can
            turn the skill into a job or a business, plus post-training support to help you get started.
          </p>
          <h2 style={{ marginTop: '2rem' }}>Who can join</h2>
          <p>
            Open to youths, women and the general public — whatever your starting point. Selection
            includes a short aptitude chat and counselling so we place you on the right course.
            Contact us for the next intake and fees.
          </p>
        </Overview>

        <Facts>
          <Fact><span>Department</span><span>{program.faculty}</span></Fact>
          <Fact><span>Level</span><span>{program.level}</span></Fact>
          <Fact><span>Duration</span><span>{program.duration}</span></Fact>
          <Fact><span>Mode</span><span>{program.mode}</span></Fact>
          <Fact><span>Tuition</span><span>{program.tuition}</span></Fact>
          <ApplyBtn to="/apply">Apply for this course →</ApplyBtn>
        </Facts>
      </Wrap>
      <Footer />
    </>
  );
};

export default ProgramDetail;
